import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import databridgeSvgDark from '../assets/databridge-diagram.svg';
import databridgeSvgLight from '../assets/databridge-diagram-light.svg';
import '../styles/productPage.css';

const TABS = ['overview', 'connectors', 'architecture', 'gettingStarted'];

const AIDataBridge = () => {
  const { t } = useTranslation('aiDataBridge');
  const { language } = useLanguage();
  const { theme } = useTheme();
  const databridgeSvg = theme === 'light' ? databridgeSvgLight : databridgeSvgDark;
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="product-page">
      <SEO
        title="E1 DataBridge – Enterprise Data Integration for AI | EWANDS"
        description="A governed integration service that transforms and feeds your enterprise digital information into structured AI databases, bridging your existing systems with your AI platform."
        keywords="E1 DataBridge, enterprise data integration, AI data pipeline, CRM ERP integration, data transformation, EWANDS"
        canonicalPath={`/${language}/e1-databridge`}
      />

      {/* Hero */}
      <section className="product-hero">
        <div className="container">
          <div className="product-hero-inner">
            <span className="product-hero-tag">{t('overview.tag')}</span>
            <h1>{t('hero.title')}</h1>
            <p className="product-hero-subtitle">{t('hero.subtitle')}</p>
            <p className="product-hero-desc">{t('hero.description')}</p>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="product-tabs-section">
        <div className="product-tab-bar-wrapper">
          <div className="container">
            <div className="product-tab-bar">
              {TABS.map((tab, i) => (
                <button
                  key={tab}
                  className={`product-tab-btn${activeTab === i ? ' active' : ''}`}
                  onClick={() => setActiveTab(i)}
                >
                  {t(`tabs.${tab}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="product-tab-content">
          <div className="container">

            {/* ── Overview ───────────────────────────────── */}
            {activeTab === 0 && (
              <div>
                <span className="pp-section-tag">{t('overview.tag')}</span>
                <h2 className="pp-section-heading">{t('overview.heading')}</h2>
                <p className="pp-section-desc">{t('overview.description')}</p>

                <div className="pp-highlights">
                  {['h1','h2','h3','h4'].map(k => (
                    <div className="pp-highlight-card" key={k}>
                      <div className="pp-highlight-label">{t(`overview.highlights.${k}.label`)}</div>
                      <div className="pp-highlight-desc">{t(`overview.highlights.${k}.desc`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Connectors ─────────────────────────────── */}
            {activeTab === 1 && (
              <div>
                <span className="pp-section-tag">{t('connectors.tag')}</span>
                <h2 className="pp-section-heading">{t('connectors.heading')}</h2>
                <p className="pp-section-desc">{t('connectors.description')}</p>

                <div className="pp-connector-grid">
                  {['crm','erp','hrms','internalApis','eventStreams','documentSources'].map(key => (
                    <div className="pp-connector-card" key={key}>
                      <span className="pp-feature-tag">{t(`connectors.items.${key}.tag`)}</span>
                      <div className="pp-connector-title">{t(`connectors.items.${key}.title`)}</div>
                      <div className="pp-connector-desc">{t(`connectors.items.${key}.description`)}</div>
                      {/* Render examples only if the key exists */}
                      {['crm','erp','hrms','eventStreams','documentSources'].includes(key) && (
                        <div className="pp-connector-examples">{t(`connectors.items.${key}.examples`)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Architecture ───────────────────────────── */}
            {activeTab === 2 && (
              <div>
                <span className="pp-section-tag">{t('architecture.tag')}</span>
                <h2 className="pp-section-heading">{t('architecture.heading')}</h2>
                <p className="pp-section-desc">{t('architecture.description')}</p>

                <div className="pp-arch-stages">
                  {['extract','validate','transform','load'].map(key => (
                    <div className="pp-arch-stage" key={key}>
                      <span className="pp-arch-stage-step">{t(`architecture.stages.${key}.step`)}</span>
                      <span className="pp-arch-stage-tag">{t(`architecture.stages.${key}.tag`)}</span>
                      <div className="pp-arch-stage-title">{t(`architecture.stages.${key}.title`)}</div>
                      <div className="pp-arch-stage-desc">{t(`architecture.stages.${key}.description`)}</div>
                    </div>
                  ))}
                </div>

                <img
                  src={databridgeSvg}
                  alt="E1 DataBridge Architecture Diagram"
                  style={{ width: '100%', borderRadius: '12px', marginTop: '2rem' }}
                />
              </div>
            )}

            {/* ── Getting Started ────────────────────────── */}
            {activeTab === 3 && (
              <div>
                <span className="pp-section-tag">{t('gettingStarted.tag')}</span>
                <h2 className="pp-section-heading">{t('gettingStarted.heading')}</h2>

                <div className="pp-onboarding-steps">
                  {['step1','step2','step3'].map(key => (
                    <div className="pp-onboarding-step" key={key}>
                      <span className="pp-onboarding-num">{t(`gettingStarted.steps.${key}.number`)}</span>
                      <div className="pp-onboarding-title">{t(`gettingStarted.steps.${key}.title`)}</div>
                      <div className="pp-onboarding-desc">{t(`gettingStarted.steps.${key}.description`)}</div>
                    </div>
                  ))}
                </div>

                <div className="pp-cta-block">
                  <div className="pp-cta-heading">{t('gettingStarted.cta.heading')}</div>
                  <p className="pp-cta-desc">{t('gettingStarted.cta.description')}</p>
                  <Link to={`/${language}/contact`} className="pp-cta-btn">
                    {t('gettingStarted.cta.button')}
                  </Link>
                </div>
              </div>
            )}

          </div>
        </div>
      </section>
    </div>
  );
};

export default AIDataBridge;
